import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  X, Send, Loader2, CheckCircle2, XCircle, AlertTriangle,
  RotateCcw, Filter, ChevronLeft, ChevronRight, Link2
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const EnvioMasivoModal = ({ open, onClose, onComplete }) => {
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [sending, setSending] = useState(false);
  const [finished, setFinished] = useState(false);
  const [results, setResults] = useState({});
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [filtroResultado, setFiltroResultado] = useState('all');
  const [filtroArquitecto, setFiltroArquitecto] = useState('all');
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(0);
  // Comunicación unificada
  const [unificablesData, setUnificablesData] = useState(null);
  const [showUnificarDialog, setShowUnificarDialog] = useState(false);
  const [unificarIdx, setUnificarIdx] = useState(0);
  const [decisionesUnificar, setDecisionesUnificar] = useState({});

  // Cargar candidatos
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get('/entregas/envio-masivo/tentativas');
      const items = resp.data || [];
      setData(items);
      // Default: filtrar por usuario logueado si está en la lista
      const arqNames = [...new Set(items.map(d => d.arquitecto_nombre).filter(Boolean))];
      const match = arqNames.find(n => n === user?.nombre);
      setFiltroArquitecto(match || 'all');
    } catch (err) {
      toast.error('Error cargando entregas con tentativas');
    } finally {
      setLoading(false);
    }
  }, [user?.nombre]);

  // Lista de arquitectos únicos
  const arquitectosUnicos = useMemo(() => {
    return [...new Set(data.map(d => d.arquitecto_nombre).filter(Boolean))].sort();
  }, [data]);

  useEffect(() => {
    if (open) {
      setSelected(new Set());
      setResults({});
      setFinished(false);
      setSending(false);
      setFiltroResultado('all');
      setFiltroArquitecto('all');
      setCurrentPage(0);
      setProgress({ current: 0, total: 0 });
      loadData();
    }
  }, [open, loadData]);

  // Filtrar datos visibles (arquitecto + resultado)
  const filteredData = useMemo(() => {
    let filtered = data;
    // Filtro por arquitecto
    if (filtroArquitecto !== 'all') {
      filtered = filtered.filter(d => d.arquitecto_nombre === filtroArquitecto);
    }
    // Filtro por resultado
    if (filtroResultado === 'pendiente') filtered = filtered.filter(d => !results[d.entrega_id]);
    else if (filtroResultado === 'ok') filtered = filtered.filter(d => results[d.entrega_id]?.ok === true);
    else if (filtroResultado === 'error') filtered = filtered.filter(d => results[d.entrega_id]?.ok === false);
    return filtered;
  }, [data, results, filtroResultado, filtroArquitecto]);

  // Paginación
  const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
  const paginatedData = useMemo(() => {
    const start = currentPage * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  // Reset página cuando cambian filtros
  useEffect(() => { setCurrentPage(0); }, [filtroArquitecto, filtroResultado, pageSize]);

  // Checkboxes — excluir exitosos de la selección
  const seleccionables = filteredData.filter(d => results[d.entrega_id]?.ok !== true);
  const allSelected = seleccionables.length > 0 && seleccionables.every(d => selected.has(d.entrega_id));
  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(seleccionables.map(d => d.entrega_id)));
    }
  };
  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Enviar propuestas
  const handleEnviar = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;

    // Paso 1: Detectar unificables
    try {
      const detectResp = await api.post('/entregas/envio-masivo/detectar-unificables', { entrega_ids: ids });
      const { unificables, individuales } = detectResp.data;

      if (unificables && unificables.length > 0) {
        setUnificablesData({ unificables, individuales });
        setUnificarIdx(0);
        setDecisionesUnificar({});
        setShowUnificarDialog(true);
        return;
      }
      await _ejecutarEnvios(ids, []);
    } catch (err) {
      console.warn('Error detectando unificables:', err);
      toast.error('Error al detectar comunicaciones unificables, enviando de forma individual');
      await _ejecutarEnvios(ids, []);
    }
  };

  const _handleDecisionUnificar = async (decision) => {
    const grupo = unificablesData.unificables[unificarIdx];
    const newDecisiones = { ...decisionesUnificar, [grupo.grupo_key]: decision };
    setDecisionesUnificar(newDecisiones);

    if (unificarIdx + 1 < unificablesData.unificables.length) {
      setUnificarIdx(unificarIdx + 1);
    } else {
      setShowUnificarDialog(false);
      const unificados = [];
      const individualesExtra = [];

      for (const g of unificablesData.unificables) {
        if (newDecisiones[g.grupo_key] === 'unificar') {
          unificados.push({ principal_id: g.principal.id, secundaria_ids: g.secundarias.map(s => s.id) });
        } else {
          individualesExtra.push(g.principal.id);
          g.secundarias.forEach(s => individualesExtra.push(s.id));
        }
      }
      const todosIndividuales = [...(unificablesData.individuales || []), ...individualesExtra];
      await _ejecutarEnvios(todosIndividuales, unificados);
    }
  };

  const _ejecutarEnvios = async (individuales, unificados) => {
    const totalOps = individuales.length + unificados.length;
    setSending(true);
    setFinished(false);
    setProgress({ current: 0, total: totalOps });
    const newResults = { ...results };
    let currentOp = 0;

    for (const uni of unificados) {
      currentOp++;
      setProgress({ current: currentOp, total: totalOps });
      newResults[uni.principal_id] = { status: 'sending' };
      uni.secundaria_ids.forEach(sid => { newResults[sid] = { status: 'sending' }; });
      setResults({ ...newResults });
      try {
        const resp = await api.post('/entregas/envio-masivo/enviar-propuesta-unificada', {
          entrega_principal_id: uni.principal_id, entrega_secundaria_ids: uni.secundaria_ids,
        });
        const r = resp.data;
        const res = { ok: r.ok, codigo: r.codigo || '', error: r.error || null, status: r.ok ? 'ok' : 'error', unificada: true };
        newResults[uni.principal_id] = res;
        uni.secundaria_ids.forEach(sid => { newResults[sid] = { ...res, unificada_sec: true }; });
      } catch (err) {
        const errRes = { ok: false, error: err?.response?.data?.detail || err.message, status: 'error' };
        newResults[uni.principal_id] = errRes;
        uni.secundaria_ids.forEach(sid => { newResults[sid] = errRes; });
      }
      setResults({ ...newResults });
    }

    for (let i = 0; i < individuales.length; i++) {
      const entregaId = individuales[i];
      currentOp++;
      setProgress({ current: currentOp, total: totalOps });
      newResults[entregaId] = { status: 'sending' };
      setResults({ ...newResults });
      try {
        const resp = await api.post('/entregas/envio-masivo/enviar-propuesta', { entrega_id: entregaId });
        const r = resp.data;
        newResults[entregaId] = { ok: r.ok, codigo: r.codigo || '', error: r.error || null, status: r.ok ? 'ok' : 'error' };
      } catch (err) {
        newResults[entregaId] = { ok: false, error: err?.response?.data?.detail || err.message || 'Error de conexión', status: 'error' };
      }
      setResults({ ...newResults });
    }

    setSending(false);
    setFinished(true);
    setSelected(new Set());
  };

  // Estadísticas
  const stats = useMemo(() => {
    const vals = Object.values(results).filter(r => r.status !== 'sending');
    return {
      total: vals.length,
      ok: vals.filter(r => r.ok).length,
      error: vals.filter(r => r.ok === false).length,
    };
  }, [results]);

  // Filtrar fallidos para reintento
  const handleFiltrarFallidos = () => {
    setFiltroResultado('error');
    const fallidos = data.filter(d => results[d.entrega_id]?.ok === false);
    setSelected(new Set(fallidos.map(d => d.entrega_id)));
  };

  // Resultado visual por fila
  const getResultadoCell = (entregaId) => {
    const r = results[entregaId];
    if (!r) return <span className="text-xs text-[#aaa]">Pendiente</span>;
    if (r.status === 'sending') return (
      <span className="flex items-center gap-1 text-xs text-[#5B7FA6]">
        <Loader2 size={12} className="animate-spin" /> Enviando...
      </span>
    );
    if (r.ok) return (
      <span className="flex items-center gap-1 text-xs text-green-600">
        <CheckCircle2 size={12} /> Enviado
      </span>
    );
    return (
      <span className="flex items-center gap-1 text-xs text-red-600" title={r.error || ''}>
        <XCircle size={12} /> {(r.error || 'Error').substring(0, 40)}
      </span>
    );
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="envio-masivo-overlay">
      <div className="bg-white rounded-xl shadow-2xl w-[95vw] max-w-[1100px] max-h-[90vh] flex flex-col" data-testid="envio-masivo-modal">
        {/* Header */}
        <div className="shrink-0 flex items-center justify-between px-5 py-4 border-b border-[#E8E8E7]">
          <div className="flex items-center gap-2">
            <Send size={18} className="text-[#5B7FA6]" />
            <h2 className="text-base font-bold text-[#2D2D2D]">Envío Masivo de Propuestas</h2>
            {!loading && <Badge variant="outline" className="text-[10px] ml-2">{filteredData.length} de {data.length}</Badge>}
          </div>
          <div className="flex items-center gap-2">
            {!loading && arquitectosUnicos.length > 1 && (
              <Select value={filtroArquitecto} onValueChange={(v) => { setFiltroArquitecto(v); setSelected(new Set()); }} disabled={sending}>
                <SelectTrigger className="w-[200px] h-8 text-xs" data-testid="filtro-arquitecto-envio">
                  <SelectValue placeholder="Arquitecto" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los arquitectos</SelectItem>
                  {arquitectosUnicos.map(name => (
                    <SelectItem key={name} value={name}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button variant="ghost" size="sm" onClick={onClose} disabled={sending} className="h-8 w-8 p-0" data-testid="btn-cerrar-envio-masivo">
              <X size={16} />
            </Button>
          </div>
        </div>

        {/* Warning during send */}
        {sending && (
          <div className="flex items-center gap-2 px-5 py-2.5 bg-amber-50 border-b border-amber-200" data-testid="sending-warning">
            <AlertTriangle size={14} className="text-amber-600 shrink-0" />
            <span className="text-xs text-amber-800 font-medium">
              No cerrar esta ventana hasta terminar el proceso de envío — Procesando {progress.current} de {progress.total}
            </span>
          </div>
        )}

        {/* Stats bar (after finish) */}
        {finished && (
          <div className="px-5 py-3 bg-[#f8f8f8] border-b border-[#E8E8E7]" data-testid="envio-stats">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <div className="text-lg font-bold text-[#2D2D2D]">{stats.total}</div>
                  <div className="text-[10px] text-[#888]">Procesados</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-green-600">{stats.ok}</div>
                  <div className="text-[10px] text-green-700">Exitosos</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-red-600">{stats.error}</div>
                  <div className="text-[10px] text-red-700">Fallidos</div>
                </div>
              </div>
              {stats.error > 0 && (
                <Button
                  variant="outline" size="sm"
                  className="ml-auto text-xs border-red-300 text-red-600 hover:bg-red-50"
                  onClick={handleFiltrarFallidos}
                  data-testid="btn-filtrar-fallidos"
                >
                  <RotateCcw size={12} className="mr-1" /> Reintentar fallidos ({stats.error})
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Filter bar */}
        {Object.keys(results).length > 0 && (
          <div className="flex items-center gap-2 px-5 py-2 border-b border-[#E8E8E7]">
            <Filter size={12} className="text-[#888]" />
            <Select value={filtroResultado} onValueChange={setFiltroResultado}>
              <SelectTrigger className="w-[160px] h-7 text-xs" data-testid="filtro-resultado-envio">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="pendiente">Pendientes</SelectItem>
                <SelectItem value="ok">Exitosos</SelectItem>
                <SelectItem value="error">Fallidos</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-[10px] text-[#888] ml-auto">{filteredData.length} registros</span>
          </div>
        )}

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={20} className="animate-spin text-[#5B7FA6]" />
              <span className="ml-2 text-sm text-[#888]">Cargando entregas con tentativas...</span>
            </div>
          ) : data.length === 0 ? (
            <div className="text-center py-16 text-[#888]">
              <Send size={36} className="mx-auto text-[#ccc] mb-3" />
              <p className="text-sm">No hay entregas con fechas tentativas listas para enviar</p>
              <p className="text-xs text-[#aaa] mt-1">Las entregas deben tener al menos una fecha tentativa y estar en estado de programación</p>
            </div>
          ) : (
            <table className="w-full text-xs" data-testid="tabla-envio-masivo">
              <thead className="sticky top-0 z-10">
                <tr className="bg-[#FAFAFA] border-b border-[#E8E8E7]">
                  <th className="text-left px-3 py-2.5 w-8">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={toggleAll}
                      disabled={sending || seleccionables.length === 0}
                      data-testid="checkbox-todos"
                    />
                  </th>
                  <th className="text-left px-3 py-2.5 font-medium text-[#666]">Código</th>
                  <th className="text-left px-3 py-2.5 font-medium text-[#666]">Proforma</th>
                  <th className="text-left px-3 py-2.5 font-medium text-[#666]">Proyecto</th>
                  <th className="text-left px-3 py-2.5 font-medium text-[#666]">Unidad</th>
                  <th className="text-left px-3 py-2.5 font-medium text-[#666]">Titular</th>
                  <th className="text-left px-3 py-2.5 font-medium text-[#666]">Email</th>
                  <th className="text-left px-3 py-2.5 font-medium text-[#666]">Tentativas</th>
                  <th className="text-left px-3 py-2.5 font-medium text-[#666]">Resultado</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map(row => {
                  const isChecked = selected.has(row.entrega_id);
                  const result = results[row.entrega_id];
                  const rowDone = result && result.status !== 'sending';
                  return (
                    <tr
                      key={row.entrega_id}
                      className={`border-b border-[#F0F0F0] transition-colors ${
                        result?.ok ? 'bg-green-50/40' : result?.ok === false ? 'bg-red-50/40' : 'hover:bg-[#FAFAFA]'
                      }`}
                      data-testid={`row-envio-${row.codigo}`}
                    >
                      <td className="px-3 py-2.5">
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={() => toggleOne(row.entrega_id)}
                          disabled={sending || result?.ok === true}
                        />
                      </td>
                      <td className="px-3 py-2.5 font-mono font-semibold text-[#5B7FA6]">{row.codigo}</td>
                      <td className="px-3 py-2.5 text-[#333]">{row.proforma_codigo || '-'}</td>
                      <td className="px-3 py-2.5 text-[#555] max-w-[140px] truncate">{row.proyecto_nombre}</td>
                      <td className="px-3 py-2.5 text-[#555]">{row.unidad_codigo || '-'}</td>
                      <td className="px-3 py-2.5 text-[#333] max-w-[140px] truncate">{row.cliente_nombre}</td>
                      <td className="px-3 py-2.5 text-[#888] max-w-[160px] truncate">{row.cliente_email || '-'}</td>
                      <td className="px-3 py-2.5 text-center">
                        <Badge variant="outline" className="text-[10px] border-[#5B7FA6]/30 text-[#5B7FA6]">
                          {row.tentativas_count}
                        </Badge>
                      </td>
                      <td className="px-3 py-2.5">{getResultadoCell(row.entrega_id)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t border-[#E8E8E7] bg-[#FAFAFA]">
          {/* Paginación */}
          <div className="flex items-center justify-between px-5 py-2 border-b border-[#f0f0f0]">
            <div className="flex items-center gap-2 text-xs text-[#888]">
              <span>Ver</span>
              <Select value={String(pageSize)} onValueChange={v => setPageSize(parseInt(v))}>
                <SelectTrigger className="w-16 h-7 text-xs" data-testid="sel-page-size">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
              <span>por página</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#888]">Página {currentPage + 1} de {totalPages}</span>
              <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={currentPage === 0} onClick={() => setCurrentPage(p => p - 1)} data-testid="btn-pag-anterior">
                <ChevronLeft size={14} />
              </Button>
              <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={currentPage >= totalPages - 1} onClick={() => setCurrentPage(p => p + 1)} data-testid="btn-pag-siguiente">
                <ChevronRight size={14} />
              </Button>
            </div>
          </div>
          {/* Acciones */}
          <div className="flex items-center justify-between px-5 py-3">
            <span className="text-xs text-[#888]">
              {selected.size} de {seleccionables.length} seleccionables
            </span>
            <div className="flex items-center gap-2">
              {finished && (
                <Button variant="outline" size="sm" onClick={() => { onComplete?.(); onClose(); }} data-testid="btn-cerrar-final">
                  Cerrar
                </Button>
              )}
              <Button
                size="sm"
                onClick={handleEnviar}
                disabled={selected.size === 0 || sending}
                className="bg-[#5B7FA6] hover:bg-[#4a6d91] text-white"
                data-testid="btn-enviar-invitaciones"
              >
                {sending ? (
                  <><Loader2 size={14} className="animate-spin mr-1" /> Enviando {progress.current}/{progress.total}</>
                ) : (
                  <><Send size={14} className="mr-1" /> Enviar invitaciones ({selected.size})</>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Dialog: Comunicación Unificable */}
      <Dialog open={showUnificarDialog} onOpenChange={() => {}}>
        <DialogContent className="max-w-lg" data-testid="dialog-unificar">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold text-[#333] flex items-center gap-2">
              <Link2 size={16} className="text-[#5B7FA6]" /> Comunicación Unificable Detectada
            </DialogTitle>
          </DialogHeader>
          {unificablesData && unificablesData.unificables[unificarIdx] && (() => {
            const g = unificablesData.unificables[unificarIdx];
            return (
              <div className="space-y-3 text-sm" data-testid="unificar-content">
                <div className="bg-[#f8f8f8] rounded p-3 space-y-1">
                  <p className="font-semibold text-[#333]">{g.titular}</p>
                  <p className="text-xs text-[#888]">Doc: {g.documento} · {g.proyecto_nombre}{g.subdivision_nombre ? ` — ${g.subdivision_nombre}` : ''}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-[#4E7A79] mb-1">Principal (Departamento):</p>
                  <div className="border border-green-200 bg-green-50/30 rounded p-2 text-xs">
                    <span className="font-mono font-bold">{g.principal.codigo}</span> · {g.principal.unidad.nombre} ({g.principal.unidad.tipo_unidad}) · {g.principal.tentativas_count} tentativa(s)
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-[#888] mb-1">Secundaria(s):</p>
                  {g.secundarias.map((s, i) => (
                    <div key={i} className="border border-[#e8e8e7] rounded p-2 text-xs mb-1">
                      <span className="font-mono font-bold">{s.codigo}</span> · {s.unidad.nombre} ({s.unidad.tipo_unidad})
                      <p className="text-[10px] text-[#aaa] mt-0.5">Sus tentativas serán reemplazadas por las del departamento.</p>
                    </div>
                  ))}
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-800">
                  Se enviará un único correo de programación que incluirá todas las unidades.
                </div>
                {unificablesData.unificables.length > 1 && (
                  <p className="text-[10px] text-[#aaa] text-right">Grupo {unificarIdx + 1} de {unificablesData.unificables.length}</p>
                )}
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" size="sm" onClick={() => _handleDecisionUnificar('ignorar')} data-testid="btn-ignorar-unificar">
                    Ignorar
                  </Button>
                  <Button size="sm" className="bg-[#5B7FA6] hover:bg-[#4a6d91] text-white" onClick={() => _handleDecisionUnificar('unificar')} data-testid="btn-unificar">
                    <Link2 size={12} className="mr-1" /> Unificar comunicación
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EnvioMasivoModal;
